import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Eye, EyeOff } from 'lucide-react'
import { useAuth } from '../context/AuthContext'

const C = {
  ink:    '#0A0A0A',
  blue:   '#4F8EF7',
  muted:  '#888888',
  muted2: '#C0C0C0',
  border: '#EBEBEB',
  bg:     '#F7F7F7',
  surface:'#FFFFFF',
  red:    '#D32F2F',
  display:"'Barlow Condensed', sans-serif",
  mono:   "'Space Grotesk', sans-serif",
}

const CSS = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html, body { font-family: 'Space Grotesk', sans-serif; background: ${C.bg}; color: ${C.ink}; }
  ::placeholder { color: ${C.muted2}; font-family: 'Space Grotesk', sans-serif; }
  @keyframes spin { to { transform: rotate(360deg); } }
  input:focus { outline: none; }
`

export default function Login() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { login } = useAuth()
  const welcomeClub = searchParams.get('welcome') === 'club'

  const [email, setEmail]         = useState('')
  const [password, setPassword]   = useState('')
  const [error, setError]         = useState('')
  const [loading, setLoading]     = useState(false)
  const [focused, setFocused]     = useState(null)
  const [showPass, setShowPass]   = useState(false)
  const [showForgot, setShowForgot]   = useState(false)
  const [forgotEmail, setForgotEmail] = useState('')
  const [forgotLoading, setForgotLoading] = useState(false)
  const [forgotSent, setForgotSent] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      await login(email, password)
      navigate('/dashboard')
    } catch (err) {
      const raw = err?.response?.data?.detail || err?.message || ''
      let msg
      if (raw === 'ACCOUNT_PERMANENTLY_DELETED') {
        msg = 'Ce compte a été définitivement supprimé.'
      } else if (raw.startsWith('ACCOUNT_DELETED:')) {
        const token = raw.split(':')[1]
        msg = token && token !== 'None'
          ? 'Ton compte est en cours de suppression. Vérifie tes emails pour le récupérer.'
          : 'Ce compte a été désactivé. Contacte le support à contact@insightball.com'
      } else {
        // `err.message` et NON `raw` : l'intercepteur axios a deja traduit les
        // details techniques du backend. Prendre `raw` ici affichait
        // « Incorrect email or password » en anglais, et rendait mort le repli
        // francais juste en dessous — exactement le meme defaut que celui
        // corrige dans friendlyMessage().
        //
        // Les deux branches ci-dessus continuent de lire `raw` : ce sont des
        // codes machine, pas des messages, et leur pilotage doit rester
        // insensible a toute reformulation d'affichage.
        msg = err?.message || 'Email ou mot de passe incorrect'
      }
      setError(msg)
      setLoading(false)
    }
  }

  const handleForgot = async (e) => {
    e.preventDefault()
    setForgotLoading(true)
    try {
      const API = import.meta.env.VITE_API_URL || 'https://backend-pued.onrender.com/api'
      await fetch(`${API}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail }),
      })
      setForgotSent(true)
    } catch {
      setForgotSent(true)
    } finally {
      setForgotLoading(false)
    }
  }

  const inputStyle = (name) => ({
    width: '100%', height: 44, padding: '0 12px',
    fontFamily: C.mono, fontSize: 13, color: C.ink,
    background: C.surface,
    border: `0.5px solid ${focused === name ? C.ink : C.border}`,
    borderRadius: 3, transition: 'border-color .12s',
  })

  const labelStyle = {
    fontFamily: C.mono, fontSize: 9, fontWeight: 600, letterSpacing: '1px',
    textTransform: 'uppercase', color: C.muted2, display: 'block', marginBottom: 6,
  }

  return (
    <div style={{ minHeight: '100vh', background: C.bg }}>
      <style>{CSS}</style>

      {/* Header */}
      <header style={{
        position: 'fixed', top: 0, left: 0, right: 0, height: 64,
        background: C.surface, borderBottom: `1.5px solid ${C.ink}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 40px', zIndex: 100,
      }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
          <img src="/logo.svg" alt="Insightball" style={{ width: 28, height: 28 }} />
          <span style={{ fontFamily: C.display, fontSize: 18, fontWeight: 800, letterSpacing: '.04em', textTransform: 'uppercase', color: C.ink }}>
            INSIGHT<span style={{ color: C.blue }}>BALL</span>
          </span>
        </Link>
        <Link to="/signup" style={{ fontFamily: C.mono, fontSize: 11, fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', color: C.ink, textDecoration: 'none', transition: 'opacity .12s' }}
          onMouseEnter={e => e.target.style.opacity = '0.55'}
          onMouseLeave={e => e.target.style.opacity = '1'}>
          Créer un compte
        </Link>
      </header>

      {/* Modale mot de passe oublié */}
      {showForgot && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 24 }}>
          <div style={{ width: '100%', maxWidth: 380, background: C.surface, border: `0.5px solid ${C.border}`, borderTop: `2.5px solid ${C.blue}`, padding: '36px 32px', borderRadius: 3 }}>
            {forgotSent ? (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: C.display, fontSize: 28, fontWeight: 800, textTransform: 'uppercase', color: C.ink, marginBottom: 12 }}>
                  Email envoyé <span style={{ color: C.blue }}>✓</span>
                </div>
                <p style={{ fontFamily: C.mono, fontSize: 12, color: C.muted, lineHeight: 1.7, marginBottom: 24 }}>
                  Si cet email est associé à un compte, vous recevrez un lien de réinitialisation.
                </p>
                <button onClick={() => { setShowForgot(false); setForgotSent(false); setForgotEmail('') }}
                  style={{ fontFamily: C.mono, fontSize: 11, fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', background: C.ink, color: '#FFFFFF', border: 'none', padding: '11px 24px', cursor: 'pointer', borderRadius: 3 }}>
                  Fermer
                </button>
              </div>
            ) : (
              <>
                <p style={{ fontFamily: C.mono, fontSize: 9, fontWeight: 600, letterSpacing: '2px', textTransform: 'uppercase', color: C.blue, marginBottom: 12 }}>Mot de passe oublié</p>
                <h2 style={{ fontFamily: C.display, fontSize: 28, fontWeight: 800, textTransform: 'uppercase', color: C.ink, marginBottom: 8, lineHeight: 1 }}>Réinitialiser.</h2>
                <p style={{ fontFamily: C.mono, fontSize: 12, color: C.muted, lineHeight: 1.7, marginBottom: 24 }}>
                  Entrez votre email et nous vous enverrons un lien pour réinitialiser votre mot de passe.
                </p>
                <form onSubmit={handleForgot}>
                  <label style={labelStyle}>Email</label>
                  <input type="email" required value={forgotEmail} onChange={e => setForgotEmail(e.target.value)}
                    placeholder="votre@email.com"
                    style={{ ...inputStyle('forgot'), marginBottom: 20 }}
                    onFocus={() => setFocused('forgot')} onBlur={() => setFocused(null)} />
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button type="button" onClick={() => setShowForgot(false)}
                      style={{ flex: 1, height: 44, background: 'transparent', border: `0.5px solid ${C.border}`, fontFamily: C.mono, fontSize: 11, letterSpacing: '.06em', textTransform: 'uppercase', color: C.muted, cursor: 'pointer', borderRadius: 3 }}>
                      Annuler
                    </button>
                    <button type="submit" disabled={forgotLoading}
                      style={{ flex: 2, height: 44, background: C.ink, border: 'none', fontFamily: C.mono, fontSize: 11, letterSpacing: '.08em', textTransform: 'uppercase', color: '#FFFFFF', fontWeight: 600, cursor: 'pointer', borderRadius: 3 }}>
                      {forgotLoading ? 'Envoi...' : 'Envoyer le lien'}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {/* Contenu */}
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '80px 24px 40px' }}>
        <div style={{ width: '100%', maxWidth: 420 }}>

          {/* Carte formulaire */}
          <div style={{
            background: C.surface, border: `0.5px solid ${C.border}`,
            borderTop: `2.5px solid ${C.blue}`, borderRadius: 3, padding: '40px 36px',
          }}>
            <p style={{ fontFamily: C.mono, fontSize: 9, fontWeight: 600, letterSpacing: '2px', textTransform: 'uppercase', color: C.blue, marginBottom: 12 }}>
              — Connexion
            </p>
            <h1 style={{ fontFamily: C.display, fontSize: 40, fontWeight: 900, textTransform: 'uppercase', lineHeight: 0.92, color: C.ink, marginBottom: 28 }}>
              Bon retour.
            </h1>

            {welcomeClub && (
              <div style={{ marginBottom: 20, padding: '12px 14px', background: '#EEF4FF', borderLeft: `2px solid ${C.blue}`, borderRadius: 2 }}>
                <p style={{ fontFamily: C.mono, fontSize: 11, color: C.blue, margin: 0, lineHeight: 1.5 }}>
                  Abonnement Club activé avec succès. Connectez-vous pour accéder à votre dashboard.
                </p>
              </div>
            )}

            {error && (
              <div style={{ marginBottom: 20, padding: '12px 14px', background: '#FFF0F0', borderLeft: `2px solid ${C.red}`, borderRadius: 2 }}>
                <p style={{ fontFamily: C.mono, fontSize: 11, color: C.red, margin: 0 }}>{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: 20 }}>
                <label style={labelStyle}>Email</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="votre@email.com" required autoComplete="email"
                  style={inputStyle('email')}
                  onFocus={() => setFocused('email')} onBlur={() => setFocused(null)} />
              </div>

              <div style={{ marginBottom: 28 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <label style={{ ...labelStyle, marginBottom: 0 }}>Mot de passe</label>
                  <button type="button" onClick={() => setShowForgot(true)}
                    style={{ fontFamily: C.mono, fontSize: 10, fontWeight: 500, color: C.blue, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                    Oublié ?
                  </button>
                </div>
                <div style={{ position: 'relative' }}>
                  <input type={showPass ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••" required autoComplete="current-password"
                    style={{ ...inputStyle('password'), paddingRight: 40 }}
                    onFocus={() => setFocused('password')} onBlur={() => setFocused(null)} />
                  <button type="button" onClick={() => setShowPass(v => !v)}
                    style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: C.muted, padding: 2, display: 'flex', alignItems: 'center' }}>
                    {showPass ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>

              <button type="submit" disabled={loading} style={{
                width: '100%', height: 44, background: loading ? '#555' : C.ink, color: '#FFFFFF',
                fontFamily: C.mono, fontSize: 12, fontWeight: 600, letterSpacing: '.5px', textTransform: 'uppercase',
                border: 'none', cursor: loading ? 'not-allowed' : 'pointer', borderRadius: 3,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, transition: 'background .12s',
              }}>
                {loading
                  ? <><span style={{ width: 12, height: 12, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin .6s linear infinite' }} /> Connexion...</>
                  : 'Se connecter →'
                }
              </button>
            </form>

            <div style={{ marginTop: 24, paddingTop: 20, borderTop: `0.5px solid ${C.border}`, textAlign: 'center' }}>
              <p style={{ fontFamily: C.mono, fontSize: 12, color: C.muted, margin: 0 }}>
                Pas encore de compte ?{' '}
                <Link to="/signup" style={{ color: C.blue, textDecoration: 'none', fontWeight: 600 }}>Créer un compte</Link>
              </p>
            </div>
          </div>

          <p style={{ textAlign: 'center', marginTop: 14, fontFamily: C.mono, fontSize: 10, color: C.muted2 }}>
            En vous connectant, vous acceptez nos{' '}
            <Link to="/cgv" style={{ color: C.muted, textDecoration: 'underline' }}>CGV</Link>
          </p>
        </div>
      </div>
    </div>
  )
}
